// Queries over a dataset's rows, run where the rows are (the screen holds its
// own copy; the server does not aggregate). Runs in the browser too: imports
// nothing from node.
//
// Promise (the same wherever a query runs; query-cases.ts holds the cases):
// - Order: where → groupBy and aggregate → orderBy → limit.
// - In `where`, a null or missing value is false in every comparison (`!=`
//   too), and values of different types are not compared ("12" is not 12).
//   Dates (`YYYY-MM-DD`) compare as text; datetimes compare by the instant.
//   `contains` is a case-sensitive substring match; `in` takes a list.
// - `groupBy` names the fields of a group (its value under the field's name);
//   a bucket rounds a date or datetime to its day, week (starting Monday) or
//   month in the dataset's time zone, written `YYYY-MM-DD` (the first day). A
//   date is the day it names, whatever the time zone. Groups come ordered by
//   their values unless `orderBy` says otherwise.
// - `aggregate` names its outputs: `count` counts rows (or the values of a
//   field), `sum` / `avg` look at numbers only, `min` / `max` at numbers, or at
//   text (dates) when there are no numbers. Over nothing, sum is 0 and the
//   others are null. With aggregates and no groupBy, the answer is one row.
// - Without groupBy or aggregate the rows come as they are, in the copy's
//   order. `orderBy` puts null last either way.
// - At most `max` rows come back; beyond that `truncated` (a `limit` within
//   `max` is not truncation).
import type { DatasetRow, DatasetSchema } from "./shared";

export type QueryOp = "=" | "!=" | "<" | "<=" | ">" | ">=" | "in" | "contains";
export type QueryBucket = "day" | "week" | "month";
export type QueryGroup = string | { field: string; bucket?: QueryBucket };
export type QueryAggregate =
  | ["count"]
  | ["count" | "sum" | "avg" | "min" | "max", string];

export interface DatasetQuery {
  where?: [string, QueryOp, unknown][];
  groupBy?: QueryGroup[];
  aggregate?: Record<string, QueryAggregate>;
  orderBy?: [string, "asc" | "desc"][];
  limit?: number;
}

export interface QueryResult {
  rows: Record<string, unknown>[];
  truncated: boolean;
}

const OPS: readonly QueryOp[] = [
  "=",
  "!=",
  "<",
  "<=",
  ">",
  ">=",
  "in",
  "contains",
];
const BUCKETS: readonly QueryBucket[] = ["day", "week", "month"];
const FNS = ["count", "sum", "avg", "min", "max"] as const;
const KEYS = new Set(["where", "groupBy", "aggregate", "orderBy", "limit"]);
const MAX_PARTS = 50;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const isName = (v: unknown): v is string =>
  typeof v === "string" && v.length > 0 && v.length <= 64;

/** Checks a query's shape (it comes from a UI's code); the error names the part. */
export function checkQuery(
  q: unknown,
): { ok: true; query: DatasetQuery } | { ok: false; error: string } {
  if (q === undefined || q === null) return { ok: true, query: {} };
  const fail = (error: string) => ({ ok: false as const, error });
  if (!isObject(q)) return fail("a query is an object");
  for (const k of Object.keys(q))
    if (!KEYS.has(k)) return fail(`unknown part ${k}`);
  if (q.where !== undefined) {
    if (!Array.isArray(q.where) || q.where.length > MAX_PARTS)
      return fail("where is a list of [field, op, value]");
    for (const w of q.where)
      if (
        !Array.isArray(w) ||
        w.length !== 3 ||
        !isName(w[0]) ||
        !OPS.includes(w[1]) ||
        (w[1] === "in" && !Array.isArray(w[2])) ||
        (w[1] === "contains" && typeof w[2] !== "string")
      )
        return fail(`where ${JSON.stringify(w)} is not [field, op, value]`);
  }
  const outputs = new Set<string>();
  if (q.groupBy !== undefined) {
    if (!Array.isArray(q.groupBy) || q.groupBy.length > MAX_PARTS)
      return fail("groupBy is a list of fields");
    for (const g of q.groupBy) {
      const field = typeof g === "string" ? g : isObject(g) ? g.field : null;
      if (!isName(field)) return fail(`groupBy ${JSON.stringify(g)}`);
      if (
        isObject(g) &&
        g.bucket !== undefined &&
        !BUCKETS.includes(g.bucket as QueryBucket)
      )
        return fail(`bucket ${String(g.bucket)} is not day, week or month`);
      outputs.add(field);
    }
  }
  if (q.aggregate !== undefined) {
    if (!isObject(q.aggregate))
      return fail("aggregate is { name: [fn, field] }");
    for (const [name, a] of Object.entries(q.aggregate)) {
      if (outputs.has(name))
        return fail(`${name} is both a group field and an aggregate`);
      if (
        !isName(name) ||
        !Array.isArray(a) ||
        !FNS.includes(a[0]) ||
        (a[0] === "count" ? a.length > 2 : a.length !== 2) ||
        (a.length === 2 && !isName(a[1]))
      )
        return fail(`aggregate ${name} is not [fn, field]`);
    }
  }
  if (q.orderBy !== undefined) {
    if (!Array.isArray(q.orderBy) || q.orderBy.length > MAX_PARTS)
      return fail("orderBy is a list of [field, asc|desc]");
    for (const o of q.orderBy)
      if (
        !Array.isArray(o) ||
        o.length !== 2 ||
        !isName(o[0]) ||
        (o[1] !== "asc" && o[1] !== "desc")
      )
        return fail(`orderBy ${JSON.stringify(o)} is not [field, asc|desc]`);
  }
  if (
    q.limit !== undefined &&
    (typeof q.limit !== "number" || !Number.isInteger(q.limit) || q.limit < 1)
  )
    return fail("limit is a whole number of at least 1");
  return { ok: true, query: q as DatasetQuery };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}T/;

/** A comparable form: datetimes become their instant, the rest stay. */
function comparable(
  value: unknown,
  datetime: boolean,
): number | string | boolean | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    if (datetime && DATETIME_RE.test(value)) {
      const ms = Date.parse(value);
      return Number.isNaN(ms) ? value : ms;
    }
    return value;
  }
  if (typeof value === "boolean") return value;
  return null;
}

type Test = (row: DatasetRow) => boolean;

function compileWhere(
  [field, op, arg]: [string, QueryOp, unknown],
  datetimes: ReadonlySet<string>,
): Test {
  const dt = datetimes.has(field);
  const same = (a: unknown, b: unknown) => {
    const x = comparable(a, dt);
    const y = comparable(b, dt);
    return x !== null && y !== null && typeof x === typeof y
      ? ([x, y] as [number, number])
      : null;
  };
  switch (op) {
    case "in": {
      const list = arg as unknown[];
      return (r) =>
        list.some((v) => {
          const p = same(r[field], v);
          return p !== null && p[0] === p[1];
        });
    }
    case "contains": {
      const text = arg as string;
      return (r) => {
        const v = r[field];
        return typeof v === "string" && v.includes(text);
      };
    }
    default:
      return (r) => {
        const p = same(r[field], arg);
        if (p === null) return false;
        const [a, b] = p;
        switch (op) {
          case "=":
            return a === b;
          case "!=":
            return a !== b;
          case "<":
            return a < b;
          case "<=":
            return a <= b;
          case ">":
            return a > b;
          case ">=":
            return a >= b;
        }
        return false;
      };
  }
}

/**
 * Rounds dates and datetimes to a day, week or month in one time zone. The
 * zone's offset is looked up once per quarter hour of time (offsets change
 * only on such marks), so a large copy costs few formatter calls.
 */
function bucketer(timezone: string) {
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  const slots = new Map<number, number>();
  /** The zone's offset at a quarter-hour mark. */
  const offsetOfSlot = (slot: number) => {
    let offset = slots.get(slot);
    if (offset === undefined) {
      const at = slot * 900_000;
      const p: Record<string, number> = {};
      for (const part of format.formatToParts(at))
        if (part.type !== "literal") p[part.type] = Number(part.value);
      offset = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - at;
      slots.set(slot, offset);
    }
    return offset;
  };
  // A UTC day whose two ends share an offset has it throughout (zones do
  // not change twice in a day), so most rows cost no formatter call.
  const days = new Map<number, number | null>();
  const offsetAt = (ms: number) => {
    const dayKey = Math.floor(ms / 86_400_000);
    let whole = days.get(dayKey);
    if (whole === undefined) {
      const first = offsetOfSlot(dayKey * 96);
      whole = first === offsetOfSlot(dayKey * 96 + 95) ? first : null;
      days.set(dayKey, whole);
    }
    return whole ?? offsetOfSlot(Math.floor(ms / 900_000));
  };
  const day = (y: number, m: number, d: number) =>
    `${String(y).padStart(4, "0")}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return (value: unknown, bucket: QueryBucket): string | null => {
    if (typeof value !== "string") return null;
    let local: Date;
    if (DATE_RE.test(value)) local = new Date(`${value}T00:00:00Z`);
    else if (DATETIME_RE.test(value)) {
      const ms = Date.parse(value);
      if (Number.isNaN(ms)) return null;
      local = new Date(ms + offsetAt(ms));
    } else return null;
    if (Number.isNaN(local.getTime())) return null;
    const y = local.getUTCFullYear();
    const m = local.getUTCMonth();
    const d = local.getUTCDate();
    if (bucket === "day") return day(y, m, d);
    if (bucket === "month") return day(y, m, 1);
    const monday = new Date(
      Date.UTC(y, m, d) - ((local.getUTCDay() + 6) % 7) * 86_400_000,
    );
    return day(
      monday.getUTCFullYear(),
      monday.getUTCMonth(),
      monday.getUTCDate(),
    );
  };
}

const typeRank = (v: unknown) =>
  typeof v === "number" ? 0 : typeof v === "string" ? 1 : 2;

/** Ascending, null and missing last. */
function compareValues(a: unknown, b: unknown): number {
  const an = a === null || a === undefined;
  const bn = b === null || b === undefined;
  if (an || bn) return an === bn ? 0 : an ? 1 : -1;
  const ra = typeRank(a);
  const rb = typeRank(b);
  if (ra !== rb) return ra - rb;
  if (typeof a === "number" || typeof a === "string")
    return a < (b as typeof a) ? -1 : a > (b as typeof a) ? 1 : 0;
  return 0;
}

interface Acc {
  count: number;
  sum: number;
  numbers: number;
  minN: number | null;
  maxN: number | null;
  minS: string | null;
  maxS: string | null;
}

const newAcc = (): Acc => ({
  count: 0,
  sum: 0,
  numbers: 0,
  minN: null,
  maxN: null,
  minS: null,
  maxS: null,
});

function add(acc: Acc, value: unknown, counted: boolean) {
  if (counted) {
    acc.count += 1;
    return;
  }
  if (value === null || value === undefined) return;
  acc.count += 1;
  if (typeof value === "number" && Number.isFinite(value)) {
    acc.sum += value;
    acc.numbers += 1;
    if (acc.minN === null || value < acc.minN) acc.minN = value;
    if (acc.maxN === null || value > acc.maxN) acc.maxN = value;
  } else if (typeof value === "string") {
    if (acc.minS === null || value < acc.minS) acc.minS = value;
    if (acc.maxS === null || value > acc.maxS) acc.maxS = value;
  }
}

function result(fn: QueryAggregate[0], acc: Acc): unknown {
  switch (fn) {
    case "count":
      return acc.count;
    case "sum":
      return acc.sum;
    case "avg":
      return acc.numbers ? acc.sum / acc.numbers : null;
    case "min":
      return acc.numbers ? acc.minN : acc.minS;
    case "max":
      return acc.numbers ? acc.maxN : acc.maxS;
  }
}

export function runQuery(
  rows: Iterable<DatasetRow>,
  q: DatasetQuery | undefined,
  opts: { timezone: string; schema: DatasetSchema | null; max: number },
): QueryResult {
  const query = q ?? {};
  const datetimes = new Set(["_at"]);
  for (const f of opts.schema?.fields ?? [])
    if (f.type === "datetime") datetimes.add(f.name);
  const tests = (query.where ?? []).map((w) => compileWhere(w, datetimes));
  const groups = (query.groupBy ?? []).map((g) =>
    typeof g === "string" ? { field: g } : g,
  );
  const aggregates = Object.entries(query.aggregate ?? {});
  let out: Record<string, unknown>[] = [];

  if (groups.length === 0 && aggregates.length === 0) {
    for (const r of rows) if (tests.every((t) => t(r))) out.push(r);
  } else {
    const round = groups.some((g) => g.bucket) ? bucketer(opts.timezone) : null;
    const byKey = new Map<string, { values: unknown[]; accs: Acc[] }>();
    if (groups.length === 0)
      byKey.set("", { values: [], accs: aggregates.map(newAcc) });
    for (const r of rows) {
      if (!tests.every((t) => t(r))) continue;
      const values = groups.map((g) => {
        const v = r[g.field];
        if (g.bucket && round) return round(v, g.bucket);
        return v === undefined ? null : v;
      });
      const key = groups.length ? JSON.stringify(values) : "";
      let group = byKey.get(key);
      if (!group) {
        group = { values, accs: aggregates.map(newAcc) };
        byKey.set(key, group);
      }
      for (const [i, [, [fn, field]]] of aggregates.entries())
        add(
          group.accs[i] as Acc,
          field ? r[field] : null,
          fn === "count" && !field,
        );
    }
    for (const { values, accs } of byKey.values()) {
      const o: Record<string, unknown> = {};
      for (const [i, g] of groups.entries()) o[g.field] = values[i];
      for (const [i, [name, [fn]]] of aggregates.entries())
        o[name] = result(fn, accs[i] as Acc);
      out.push(o);
    }
    if (!query.orderBy && groups.length)
      out.sort((a, b) => {
        for (const g of groups) {
          const c = compareValues(a[g.field], b[g.field]);
          if (c) return c;
        }
        return 0;
      });
  }

  if (query.orderBy?.length) {
    const order = query.orderBy;
    out = [...out].sort((a, b) => {
      for (const [field, dir] of order) {
        const x = a[field];
        const y = b[field];
        const xn = x === null || x === undefined;
        const yn = y === null || y === undefined;
        // null last whichever way
        if (xn || yn) {
          if (xn !== yn) return xn ? 1 : -1;
          continue;
        }
        const c = compareValues(x, y);
        if (c) return dir === "desc" ? -c : c;
      }
      return 0;
    });
  }
  const limit = query.limit ?? Number.POSITIVE_INFINITY;
  const take = Math.min(limit, opts.max);
  const truncated = out.length > take && take < limit;
  return { rows: out.length > take ? out.slice(0, take) : out, truncated };
}
